/**
 * Job Offer Banner — slides in from top when a new job is available.
 * Shown on the Home screen when the driver is online.
 */
import React, { useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Animated, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, radii, RD } from '../theme/colors';

interface JobOfferBannerProps {
  visible: boolean;
  job: {
    productTitle: string;
    payoutAmount: number;
    _distanceMiles: number;
    productCategory: string;
  } | null;
  onPress: () => void;
  onDismiss: () => void;
}

export function JobOfferBanner({ visible, job, onPress, onDismiss }: JobOfferBannerProps) {
  const slideAnim = useRef(new Animated.Value(-120)).current;

  useEffect(() => {
    Animated.spring(slideAnim, {
      toValue: visible ? 0 : -120,
      useNativeDriver: true,
      tension: 60,
      friction: 12,
    }).start();
  }, [visible]);

  if (!job) return null;

  return (
    <Animated.View style={[styles.container, { transform: [{ translateY: slideAnim }] }]}>
      <TouchableOpacity style={styles.card} onPress={onPress} activeOpacity={0.85}>
        <View style={styles.iconWrap}>
          <Ionicons name="flash" size={20} color="#fff" />
        </View>
        <View style={styles.content}>
          <Text style={styles.label}>New Job Available</Text>
          <Text style={styles.title} numberOfLines={1}>{job.productTitle || 'New job'}</Text>
          <View style={styles.metaRow}>
            <Text style={styles.payout}>${Math.round(job.payoutAmount ?? 0)}</Text>
            {job._distanceMiles != null && (
              <Text style={styles.distance}>{job._distanceMiles.toFixed(1)} mi</Text>
            )}
          </View>
        </View>
        <TouchableOpacity onPress={onDismiss} style={styles.dismissBtn} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <Ionicons name="close" size={18} color="rgba(255,255,255,0.6)" />
        </TouchableOpacity>
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container:  { position: 'absolute', top: Platform.OS === 'ios' ? 50 : 30, left: 16, right: 16, zIndex: 100 },
  card:       { backgroundColor: RD.ink, borderRadius: 18, padding: spacing.md, flexDirection: 'row', alignItems: 'center', gap: 12, shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 16, shadowOffset: { width: 0, height: 6 }, elevation: 8 },
  iconWrap:   { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.14)', justifyContent: 'center', alignItems: 'center' },
  content:    { flex: 1 },
  label:      { fontSize: 11, color: 'rgba(255,255,255,0.72)', fontWeight: '600', letterSpacing: 0.3 },
  title:      { fontSize: 15, color: '#fff', fontWeight: '700', marginTop: 1 },
  metaRow:    { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 4 },
  payout:     { fontSize: 14, color: RD.moneySoft, fontWeight: '700' },
  distance:   { fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: '500' },
  dismissBtn: { padding: 4 },
});
